<template>
    <section
        class="some_details_of_group_wrp group_info_panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="group-info-title"
        @click="closeUserMenu"
    >
        <header class="group_info_panel__header">
            <button
                type="button"
                class="group_info_panel__close"
                aria-label="Close group information"
                title="Close"
                @click="closeGroupInfo"
            >
                <i class="fa fa-times" aria-hidden="true" />
            </button>

            <div class="group_info_panel__heading">
                <h2 id="group-info-title">Group Info</h2>
                <span>{{ participantLabel }}</span>
            </div>
        </header>

        <div class="group_info_panel__content">
            <section class="group_profile_card">
                <div class="group_profile_card__hero">
                    <img
                        :src="groupImage"
                        alt=""
                        class="group_profile_card__backdrop"
                        aria-hidden="true"
                        @error="handleGroupImageError"
                    >

                    <div class="group_profile_card__overlay" aria-hidden="true" />

                    <div class="group_profile_card__avatar_shell">
                        <img
                            :src="groupImage"
                            :alt="groupName"
                            class="group_profile_card__avatar"
                            @error="handleGroupImageError"
                        >

                        <button
                            v-if="canManageGroup"
                            type="button"
                            class="group_profile_card__camera"
                            aria-label="Change group image"
                            title="Change group image"
                            @click.stop="openUpdateGroupIconPopup"
                        >
                            <i class="fa fa-camera" aria-hidden="true" />
                        </button>
                    </div>
                </div>

                <div class="group_profile_card__body">
                    <h3 :title="groupName">{{ groupName }}</h3>

                    <p v-if="groupInfo.created_at" class="group_profile_card__date">
                        Created {{ groupInfo.created_at | dateformat }}
                        at {{ groupInfo.created_at | chattimeformat }}
                    </p>

                    <div
                        v-if="canManageGroup"
                        class="group_profile_card__management"
                        role="group"
                        aria-label="Group management actions"
                    >
                        <button
                            type="button"
                            @click.stop="openUpdateGroupNamePopup"
                        >
                            <span class="group_profile_card__management_icon">
                                <i class="fa fa-pencil" aria-hidden="true" />
                            </span>
                            <span>
                                <strong>Edit name</strong>
                                <small>Update group title</small>
                            </span>
                        </button>

                        <button
                            type="button"
                            @click.stop="openUpdateGroupIconPopup"
                        >
                            <span class="group_profile_card__management_icon">
                                <i class="fa fa-picture-o" aria-hidden="true" />
                            </span>
                            <span>
                                <strong>Change photo</strong>
                                <small>Update group image</small>
                            </span>
                        </button>
                    </div>
                </div>
            </section>

            <section class="participants_card" aria-labelledby="participants-title">
                <div class="participants_card__header">
                    <div>
                        <h3 id="participants-title">Participants</h3>
                        <span class="participants_card__count">
                            {{ participantCount }}
                        </span>
                    </div>

                    <button
                        v-if="canManageGroup"
                        type="button"
                        class="participants_card__add"
                        @click.stop="openUpdateGroupInfoPopup"
                    >
                        <span class="participants_card__add_icon">
                            <i class="fa fa-user-plus" aria-hidden="true" />
                        </span>
                        <span>Add participant</span>
                    </button>
                </div>

                <ul class="participants_list">
                    <li
                        v-for="(groupUserItem, index) in groupUsers"
                        :key="getGroupUserKey(groupUserItem, index)"
                        class="participant_row"
                    >
                        <img
                            :src="getUserThumbnail(groupUserItem)"
                            :alt="getUserFullName(groupUserItem)"
                            class="participant_row__avatar"
                            @error="handleUserImageError"
                        >

                        <span class="participant_row__details">
                            <span
                                class="participant_row__name"
                                :title="getUserFullName(groupUserItem)"
                            >
                                {{ getUserFullName(groupUserItem) }}
                            </span>

                            <span
                                v-if="getUserDesignation(groupUserItem)"
                                class="participant_row__designation"
                                :title="getUserDesignation(groupUserItem)"
                            >
                                {{ getUserDesignation(groupUserItem) }}
                            </span>
                        </span>

                        <div class="participant_row__actions">
                            <span
                                v-if="isGroupAdmin(groupUserItem)"
                                class="admin_badge"
                            >
                                Admin
                            </span>

                            <div
                                v-if="canManageUser(groupUserItem)"
                                class="participant_menu"
                            >
                                <button
                                    type="button"
                                    class="participant_menu__toggle"
                                    :id="getMenuButtonId(groupUserItem, index)"
                                    :aria-expanded="isUserMenuOpen(groupUserItem, index)"
                                    aria-haspopup="menu"
                                    :aria-label="'Manage ' + getUserFullName(groupUserItem)"
                                    @click.stop="toggleUserMenu(groupUserItem, index)"
                                >
                                    <i class="fa fa-ellipsis-v" aria-hidden="true" />
                                </button>

                                <transition name="participant-menu">
                                    <div
                                        v-if="isUserMenuOpen(groupUserItem, index)"
                                        class="participant_menu__popup"
                                        :class="{
                                            'participant_menu__popup--up':
                                                shouldOpenUserMenuUp(index)
                                        }"
                                        role="menu"
                                        :aria-labelledby="getMenuButtonId(groupUserItem, index)"
                                        @click.stop
                                    >
                                        <button
                                            v-if="isGroupAdmin(groupUserItem)"
                                            type="button"
                                            role="menuitem"
                                            @click="openDismissAdminPopup(getUser(groupUserItem))"
                                        >
                                            <i class="fa fa-user-times" aria-hidden="true" />
                                            <span>Dismiss as admin</span>
                                        </button>

                                        <button
                                            v-else
                                            type="button"
                                            role="menuitem"
                                            @click="openMakeAdminPopup(getUser(groupUserItem))"
                                        >
                                            <i class="fa fa-shield" aria-hidden="true" />
                                            <span>Make group admin</span>
                                        </button>

                                        <button
                                            type="button"
                                            class="participant_menu__danger"
                                            role="menuitem"
                                            @click="openRemoveUserPopup(getUser(groupUserItem))"
                                        >
                                            <i class="fa fa-user-times" aria-hidden="true" />
                                            <span>Remove participant</span>
                                        </button>
                                    </div>
                                </transition>
                            </div>
                        </div>
                    </li>
                </ul>

                <p v-if="!groupUsers.length" class="participants_card__empty">
                    No participants are available.
                </p>
            </section>

            <button
                v-if="isGroupCreator"
                type="button"
                class="group_danger_action"
                @click.stop="openDeleteGroupPopup"
            >
                <span class="group_danger_action__icon">
                    <i class="fa fa-trash" aria-hidden="true" />
                </span>
                <span>
                    <strong>Delete group</strong>
                    <small>Remove this group and its conversation</small>
                </span>
                <i class="fa fa-angle-right group_danger_action__arrow" aria-hidden="true" />
            </button>

            <button
                v-else
                type="button"
                class="group_danger_action"
                @click.stop="openLeaveGroupPopup"
            >
                <span class="group_danger_action__icon">
                    <i class="fa fa-sign-out" aria-hidden="true" />
                </span>
                <span>
                    <strong>Exit group</strong>
                    <small>You will stop receiving messages from this group</small>
                </span>
                <i class="fa fa-angle-right group_danger_action__arrow" aria-hidden="true" />
            </button>
        </div>

        <update-group-info
            v-if="popupState.updateGroupInfo"
            :update-group-info-i-d="groupChatRoomId"
        />

        <update-group-name
            v-if="popupState.updateGroupName"
            :update-group-name-i-d="groupChatRoomId"
        />

        <update-group-icon
            v-if="popupState.updateGroupIcon"
            :update-group-icon-i-d="groupChatRoomId"
        />

        <remove-user-popup
            v-if="popupState.removeUser"
            :group-info="groupInfo"
            :user-info="selectedUser"
        />

        <leave-group-popup
            v-if="popupState.leaveGroup"
            :group-info="groupInfo"
        />

        <make-admin-popup
            v-if="popupState.makeAdmin"
            :group-info="groupInfo"
            :user-info="selectedUser"
        />

        <dismiss-admin-popup
            v-if="popupState.dismissAdmin"
            :group-info="groupInfo"
            :user-info="selectedUser"
        />

        <delete-group-popup
            v-if="popupState.deleteGroup"
            :group-info="groupInfo"
        />
    </section>
</template>

<script>
import { bus } from '@/main';
import defaultGroupImage from '@/assets/images/user_default.png';
import defaultUserImage from '@/assets/images/user_circle_avtar.png';

const UpdateGroupName = () =>
    import('@/components/private/workspace/group/update-group-name/');

const UpdateGroupInfo = () =>
    import('@/components/private/workspace/group/update-group-info/');

const UpdateGroupIcon = () =>
    import('@/components/private/workspace/group/update-group-icon/');

const RemoveUserPopup = () =>
    import('@/components/private/workspace/group/remove-user-from-group/');

const LeaveGroupPopup = () =>
    import('@/components/private/workspace/group/user-leave-group-popup/');

const MakeAdminPopup = () =>
    import('@/components/private/workspace/group/user-make-admin-popup/');

const DismissAdminPopup = () =>
    import('@/components/private/workspace/group/dismiss-admin-permission-popup/');

// Keep this path aligned with the existing project directory name.
const DeleteGroupPopup = () =>
    import('@/components/private/workspace/group/delete-group-popop/');

const POPUP_NAMES = Object.freeze([
    'updateGroupInfo',
    'updateGroupName',
    'updateGroupIcon',
    'removeUser',
    'leaveGroup',
    'makeAdmin',
    'dismissAdmin',
    'deleteGroup'
]);

const BUS_EVENT_HANDLERS = Object.freeze({
    updateGroupInfoDataBus: 'handleGroupInfoUpdated',
    updateGroupIconPopupStateBus: 'handleUpdateGroupIconPopupState',
    updateGroupInfoPopupStateBus: 'handleUpdateGroupInfoPopupState',
    updateGroupNamePopupStateBus: 'handleUpdateGroupNamePopupState',
    removeUserPopupStateBus: 'handleRemoveUserPopupState',
    leaveGroupPopupStateBus: 'handleLeaveGroupPopupState',
    makeUsrToAdminPopupStateBus: 'handleMakeAdminPopupState',
    dismissAdminPermissionStateBus: 'handleDismissAdminPopupState',
    deleteGroupInfoPopupBus: 'handleDeleteGroupPopupState'
});

function createPopupState() {
    return POPUP_NAMES.reduce((state, popupName) => {
        state[popupName] = false;
        return state;
    }, {});
}

function firstValue(values) {
    return values.find((value) => {
        return (
            value !== undefined &&
            value !== null &&
            String(value).trim() !== ''
        );
    });
}

function toBoolean(value) {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value === 1;
    }

    if (typeof value === 'string') {
        return ['1', 'true', 'yes'].indexOf(
            value.trim().toLowerCase()
        ) !== -1;
    }

    return false;
}

function getStoredUserId() {
    try {
        return String(localStorage.getItem('LoginedUserID') || '');
    } catch (error) {
        return '';
    }
}

export default {
    name: 'GroupInfoPreviewPopup',

    components: {
        UpdateGroupInfo,
        UpdateGroupName,
        UpdateGroupIcon,
        RemoveUserPopup,
        LeaveGroupPopup,
        MakeAdminPopup,
        DismissAdminPopup,
        DeleteGroupPopup
    },

    props: {
        selectedRoomInfo: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            selfUserId: getStoredUserId(),
            groupInfo: Object.assign({}, this.selectedRoomInfo || {}),
            selectedUser: null,
            popupState: createPopupState(),
            openUserMenuKey: null,
            groupImageFailed: false
        };
    },

    computed: {
        groupUsers() {
            return Array.isArray(this.groupInfo.users)
                ? this.groupInfo.users.filter(Boolean)
                : [];
        },

        participantCount() {
            return this.groupUsers.length;
        },

        participantLabel() {
            return this.participantCount === 1
                ? '1 participant'
                : `${this.participantCount} participants`;
        },

        groupName() {
            return String(
                firstValue([
                    this.groupInfo.group_name,
                    this.groupInfo.name,
                    this.groupInfo.title
                ]) || 'Unnamed Group'
            );
        },

        groupImage() {
            if (this.groupImageFailed) {
                return defaultGroupImage;
            }

            const image = firstValue([
                this.groupInfo.group_image,
                this.groupInfo.image,
                this.groupInfo.icon
            ]);

            if (image && typeof image === 'object') {
                return firstValue([
                    image.thumbnail,
                    image.url,
                    image.original
                ]) || defaultGroupImage;
            }

            return image ? String(image) : defaultGroupImage;
        },

        groupChatRoomId() {
            return firstValue([
                this.groupInfo.chat_room,
                this.groupInfo.room_id,
                this.groupInfo.id
            ]) || null;
        },

        groupCreatorId() {
            return this.resolveParticipantUserId(
                this.groupInfo.created_by
            );
        },

        isGroupCreator() {
            return this.isSameUser(
                this.groupCreatorId,
                this.selfUserId
            );
        },

        currentUserGroupRecord() {
            return this.groupUsers.find((groupUserItem) => {
                return this.isSameUser(
                    this.resolveParticipantUserId(groupUserItem),
                    this.selfUserId
                );
            }) || null;
        },

        isCurrentUserAdmin() {
            return Boolean(
                this.currentUserGroupRecord &&
                toBoolean(this.currentUserGroupRecord.is_admin)
            );
        },

        canManageGroup() {
            return this.isGroupCreator || this.isCurrentUserAdmin;
        },

        hasOpenPopup() {
            return POPUP_NAMES.some((popupName) => {
                return this.popupState[popupName];
            });
        }
    },

    watch: {
        selectedRoomInfo: {
            immediate: true,

            handler(roomInfo) {
                this.groupInfo = Object.assign({}, roomInfo || {});
                this.groupImageFailed = false;
                this.closeUserMenu();
            }
        }
    },

    mounted() {
        this.registerBusEvents();
        window.addEventListener('keydown', this.handleWindowKeydown);
    },

    beforeDestroy() {
        this.unregisterBusEvents();
        window.removeEventListener('keydown', this.handleWindowKeydown);
    },

    methods: {
        registerBusEvents() {
            Object.keys(BUS_EVENT_HANDLERS).forEach((eventName) => {
                bus.$on(
                    eventName,
                    this[BUS_EVENT_HANDLERS[eventName]]
                );
            });
        },

        unregisterBusEvents() {
            Object.keys(BUS_EVENT_HANDLERS).forEach((eventName) => {
                bus.$off(
                    eventName,
                    this[BUS_EVENT_HANDLERS[eventName]]
                );
            });
        },

        resolveUserId(value) {
            if (value === undefined || value === null) {
                return null;
            }

            if (typeof value !== 'object') {
                return value;
            }

            const directId = firstValue([
                value.user_id,
                value.id,
                value.pk
            ]);

            if (directId !== undefined) {
                return directId;
            }

            if (value.user && value.user !== value) {
                return this.resolveUserId(value.user);
            }

            return null;
        },

        resolveParticipantUserId(groupUserItem) {
            if (
                groupUserItem &&
                typeof groupUserItem === 'object' &&
                groupUserItem.user &&
                groupUserItem.user !== groupUserItem
            ) {
                return this.resolveUserId(groupUserItem.user);
            }

            return this.resolveUserId(groupUserItem);
        },

        isSameUser(firstUserId, secondUserId) {
            if (
                firstUserId === undefined ||
                firstUserId === null ||
                secondUserId === undefined ||
                secondUserId === null
            ) {
                return false;
            }

            return String(firstUserId) === String(secondUserId);
        },

        getUser(groupUserItem) {
            if (!groupUserItem || typeof groupUserItem !== 'object') {
                return {};
            }

            return groupUserItem.user &&
                typeof groupUserItem.user === 'object'
                ? groupUserItem.user
                : groupUserItem;
        },

        getUserProfile(groupUserItem) {
            const user = this.getUser(groupUserItem);

            return user.profile && typeof user.profile === 'object'
                ? user.profile
                : {};
        },

        getUserThumbnail(groupUserItem) {
            const user = this.getUser(groupUserItem);
            const profile = this.getUserProfile(groupUserItem);
            const image = firstValue([
                profile.thumbnail,
                profile.image,
                user.thumbnail,
                user.profile_image,
                user.avatar
            ]);

            return image ? String(image) : defaultUserImage;
        },

        getUserDesignation(groupUserItem) {
            const user = this.getUser(groupUserItem);
            const profile = this.getUserProfile(groupUserItem);

            return String(
                firstValue([
                    profile.designation,
                    user.designation,
                    user.job_title
                ]) || ''
            );
        },

        getUserFullName(groupUserItem) {
            const user = this.getUser(groupUserItem);
            const fullName = [
                user.first_name,
                user.last_name
            ]
                .filter(Boolean)
                .join(' ')
                .trim();

            return String(
                fullName ||
                firstValue([
                    user.full_name,
                    user.name,
                    user.handle,
                    user.username
                ]) ||
                'Unknown User'
            );
        },

        getGroupUserKey(groupUserItem, index) {
            const participantId = this.resolveUserId(groupUserItem);

            if (participantId !== null) {
                return `participant_${participantId}`;
            }

            return `participant_index_${index}`;
        },

        getMenuButtonId(groupUserItem, index) {
            return `participant_menu_${this.getGroupUserKey(
                groupUserItem,
                index
            )}`;
        },

        getUserMenuKey(groupUserItem, index) {
            return this.getGroupUserKey(groupUserItem, index);
        },

        isGroupAdmin(groupUserItem) {
            return (
                this.isSameUser(
                    this.resolveParticipantUserId(groupUserItem),
                    this.groupCreatorId
                ) ||
                toBoolean(groupUserItem && groupUserItem.is_admin)
            );
        },

        canManageUser(groupUserItem) {
            if (!this.canManageGroup || !groupUserItem) {
                return false;
            }

            const userId = this.resolveParticipantUserId(groupUserItem);

            if (userId === null) {
                return false;
            }

            // Neither the current user nor the permanent creator can be
            // changed through participant actions.
            return (
                !this.isSameUser(userId, this.selfUserId) &&
                !this.isSameUser(userId, this.groupCreatorId)
            );
        },

        toggleUserMenu(groupUserItem, index) {
            const menuKey = this.getUserMenuKey(groupUserItem, index);

            this.openUserMenuKey = this.openUserMenuKey === menuKey
                ? null
                : menuKey;
        },

        isUserMenuOpen(groupUserItem, index) {
            return this.openUserMenuKey ===
                this.getUserMenuKey(groupUserItem, index);
        },

        shouldOpenUserMenuUp(index) {
            return index >= Math.max(0, this.groupUsers.length - 2);
        },

        closeUserMenu() {
            this.openUserMenuKey = null;
        },

        handleWindowKeydown(event) {
            if (!event || event.key !== 'Escape') {
                return;
            }

            if (this.openUserMenuKey !== null) {
                this.closeUserMenu();
                return;
            }

            if (!this.hasOpenPopup) {
                this.closeGroupInfo();
            }
        },

        handleGroupImageError() {
            this.groupImageFailed = true;
        },

        handleUserImageError(event) {
            const image = event && event.target;

            if (!image || image.dataset.fallbackApplied === 'true') {
                return;
            }

            image.dataset.fallbackApplied = 'true';
            image.src = defaultUserImage;
        },

        setPopupState(popupName, state) {
            if (POPUP_NAMES.indexOf(popupName) === -1) {
                return;
            }

            this.$set(this.popupState, popupName, Boolean(state));
        },

        closeAllPopups() {
            this.popupState = createPopupState();
            this.selectedUser = null;
        },

        openPopup(popupName) {
            this.closeUserMenu();
            this.closeAllPopups();
            this.setPopupState(popupName, true);
        },

        openUserActionPopup(popupName, userInfo) {
            const userId = this.resolveUserId(userInfo);

            if (userId === null) {
                return;
            }

            this.closeUserMenu();
            this.closeAllPopups();
            this.selectedUser = userInfo;
            this.setPopupState(popupName, true);
        },

        closeGroupInfo() {
            this.closeUserMenu();
            bus.$emit('groupInfoStateBus', false);
        },

        openUpdateGroupInfoPopup() {
            this.openPopup('updateGroupInfo');
        },

        openUpdateGroupNamePopup() {
            this.openPopup('updateGroupName');
        },

        openUpdateGroupIconPopup() {
            this.openPopup('updateGroupIcon');
        },

        openRemoveUserPopup(userInfo) {
            this.openUserActionPopup('removeUser', userInfo);
        },

        openMakeAdminPopup(userInfo) {
            this.openUserActionPopup('makeAdmin', userInfo);
        },

        openDismissAdminPopup(userInfo) {
            this.openUserActionPopup('dismissAdmin', userInfo);
        },

        openLeaveGroupPopup() {
            this.openPopup('leaveGroup');
        },

        openDeleteGroupPopup() {
            this.openPopup('deleteGroup');
        },

        handleGroupInfoUpdated(groupInfo) {
            if (!groupInfo || typeof groupInfo !== 'object') {
                return;
            }

            const incomingRoomId = firstValue([
                groupInfo.chat_room,
                groupInfo.room_id
            ]);

            if (
                incomingRoomId !== undefined &&
                this.groupChatRoomId !== null &&
                String(incomingRoomId) !== String(this.groupChatRoomId)
            ) {
                return;
            }

            this.groupInfo = Object.assign(
                {},
                this.groupInfo,
                groupInfo
            );
            this.groupImageFailed = false;
        },

        handleUpdateGroupIconPopupState(state) {
            this.setPopupState('updateGroupIcon', state);
        },

        handleUpdateGroupInfoPopupState(state) {
            this.setPopupState('updateGroupInfo', state);
        },

        handleUpdateGroupNamePopupState(state) {
            this.setPopupState('updateGroupName', state);
        },

        handleRemoveUserPopupState(state) {
            this.setPopupState('removeUser', state);

            if (!state) {
                this.selectedUser = null;
            }
        },

        handleLeaveGroupPopupState(state) {
            this.setPopupState('leaveGroup', state);
        },

        handleMakeAdminPopupState(state) {
            this.setPopupState('makeAdmin', state);

            if (!state) {
                this.selectedUser = null;
            }
        },

        handleDismissAdminPopupState(state) {
            this.setPopupState('dismissAdmin', state);

            if (!state) {
                this.selectedUser = null;
            }
        },

        handleDeleteGroupPopupState(state) {
            this.setPopupState('deleteGroup', state);
        }
    }
};
</script>

<style lang="scss" scoped>
$panel-bg: #0b0d0f;
$card-bg: #111111;
$card-hover: #191d21;
$surface: #20252a;
$border: rgba(255, 255, 255, 0.04);
$text: #f5f7f8;
$muted: #9aa4ad;
$blue: #87cefa;
$yellow: #ffd400;
$danger: #ff4d45;
$success: #29c779;

.group_info_panel {
    position: absolute;
    inset: 0;
    z-index: 111;
    display: flex;
    min-width: 0;
    height: 100%;
    overflow: hidden;
    flex-direction: column;
    color: $text;
    background: $panel-bg;
    font-family:
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        'Helvetica Neue',
        Arial,
        sans-serif;
}

.group_info_panel__header {
    position: relative;
    z-index: 4;
    display: flex;
    min-height: 60px;
    padding: 0 20px;
    // border-bottom: 1px solid $border;
    align-items: center;
    flex: 0 0 60px;
    background: #111111;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.18);
}

.group_info_panel__close,
.group_profile_card__camera,
.group_profile_card__management button,
.participants_card__add,
.participant_menu__toggle,
.participant_menu__popup button,
.group_danger_action {
    padding: 0;
    border: 0;
    outline: 0;
    color: inherit;
    background: transparent;
    font: inherit;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}

.group_info_panel__close {
    display: inline-flex;
    width: 36px;
    height: 36px;
    margin-right: 12px;
    border: 1px solid $border;
    border-radius: 9px;
    align-items: center;
    justify-content: center;
    color: $muted;
    background: rgba(255, 255, 255, 0.035);
    transition:
        border-color 160ms ease,
        color 160ms ease,
        background-color 160ms ease;

    &:hover,
    &:focus-visible {
        border-color: rgba(255, 77, 69, 0.5);
        color: $danger;
        background: rgba(255, 77, 69, 0.08);
    }

    .fa {
        font-size: 17px;
    }
}

.group_info_panel__heading {
    min-width: 0;

    h2,
    span {
        display: block;
    }

    h2 {
        margin: 0;
        color: $text;
        font-size: 18px;
        font-weight: 600;
        line-height: 1.2;
    }

    span {
        margin-top: 2px;
        color: $muted;
        font-size: 12px;
        line-height: 1.2;
    }
}

.group_info_panel__content {
    min-height: 0;
    padding: 15px;
    overflow-x: hidden;
    overflow-y: auto;
    flex: 1 1 auto;
    scrollbar-color: #3b4248 transparent;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
        width: 7px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        border: 2px solid transparent;
        border-radius: 10px;
        background: #3b4248;
        background-clip: padding-box;
    }
}

.group_profile_card,
.participants_card {
    border: 1px solid $border;
    border-radius: 12px;
    background: $card-bg;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
}

.group_profile_card {
    overflow: hidden;
}

.group_profile_card__hero {
    position: relative;
    height: 70px;
    margin-bottom: 50px;
    background:
        linear-gradient(
            135deg,
            rgba(112, 200, 255, 0.17),
            rgba(255, 212, 0, 0.06)
        ),
        $surface;
}

.group_profile_card__hero {
    overflow: visible;
}

.group_profile_card__backdrop {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    opacity: 0.5;
    filter: blur(14px) saturate(0.7) brightness(0.66);
    clip-path: inset(0);
    object-fit: cover;
    object-position: center;
}

.group_profile_card__overlay {
    position: absolute;
    inset: 0;
    background:
        linear-gradient(
            180deg,
            rgba(11, 13, 15, 0.12),
            rgba(11, 13, 15, 0.82)
        ),
        linear-gradient(
            90deg,
            rgba(112, 200, 255, 0.1),
            transparent 50%,
            rgba(255, 212, 0, 0.08)
        );
}

.group_profile_card__avatar_shell {
    position: absolute;
    bottom: -47px;
    left: 50%;
    z-index: 2;
    width: 102px;
    height: 102px;
    padding: 4px;
    border-radius: 50%;
    background: $card-bg;
    box-shadow:
        0 10px 26px rgba(0, 0, 0, 0.45),
        0 0 0 1px rgba(255, 255, 255, 0.12);
    transform: translateX(-50%);
}

.group_profile_card__avatar {
    display: block;
    width: 100%;
    height: 100%;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 50%;
    object-fit: cover;
    object-position: center;
    background: $surface;
}

.group_profile_card__camera {
    position: absolute;
    right: -2px;
    bottom: 2px;
    display: inline-flex;
    width: 31px;
    height: 31px;
    border: 3px solid $card-bg;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    color: #101214;
    background: $yellow;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
    font-size: 12px;
    transition:
        transform 160ms ease,
        background-color 160ms ease;

    &:hover,
    &:focus-visible {
        background: #ffe34c;
        transform: scale(1.08);
    }
}

.group_profile_card__body {
    padding: 0 17px 17px;
    text-align: center;

    > h3 {
        margin: 0;
        color: $text;
        font-size: 19px;
        font-weight: 600;
        line-height: 1.35;
        overflow-wrap: anywhere;
    }
}

.group_profile_card__date {
    margin: 4px 0 0;
    color: $muted;
    font-size: 12px;
    line-height: 1.4;
}

.group_profile_card__management {
    display: grid;
    margin-top: 10px;
    padding-top: 10px;
    // border-top: 1px solid $border;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 9px;

    button {
        display: flex;
        min-width: 0;
        min-height: 52px;
        padding: 8px 10px;
        border: 1px solid $border;
        border-radius: 9px;
        align-items: center;
        gap: 9px;
        color: $text;
        background: rgba(255, 255, 255, 0.025);
        text-align: left;
        transition:
            border-color 160ms ease,
            background-color 160ms ease,
            transform 160ms ease;

        &:hover,
        &:focus-visible {
            border-color: rgba(112, 200, 255, 0.42);
            background: rgba(112, 200, 255, 0.06);
            transform: translateY(-1px);

            .group_profile_card__management_icon {
                color: $yellow;
                background: rgba(255, 212, 0, 0.12);
            }
        }

        > span:last-child {
            display: block;
            min-width: 0;
        }

        strong,
        small {
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        strong {
            color: $text;
            font-size: 12px;
            font-weight: 600;
            line-height: 1.25;
        }

        small {
            margin-top: 2px;
            color: $muted;
            font-size: 10px;
            line-height: 1.2;
        }
    }
}

.group_profile_card__management_icon {
    display: inline-flex;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    align-items: center;
    justify-content: center;
    flex: 0 0 32px;
    color: $blue;
    background: rgba(112, 200, 255, 0.09);
    transition:
        color 160ms ease,
        background-color 160ms ease;
}

.participants_card {
    margin-top: 15px;
    padding: 0 16px;
}

.participants_card__header {
    display: flex;
    min-height: 66px;
    border-bottom: 1px solid $border;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    > div {
        display: flex;
        min-width: 0;
        align-items: center;
        gap: 8px;
    }

    h3 {
        margin: 0;
        color: $text;
        font-size: 16px;
        font-weight: 600;
        line-height: 1.2;
    }
}

.participants_card__count {
    display: inline-flex;
    min-width: 24px;
    height: 24px;
    padding: 0 7px;
    border-radius: 12px;
    align-items: center;
    justify-content: center;
    color: $blue;
    background: rgba(112, 200, 255, 0.12);
    font-size: 12px;
    font-weight: 700;
}

.participants_card__add {
    display: inline-flex;
    min-height: 36px;
    padding: 0 12px 0 8px;
    border: 1px solid rgba(255, 212, 0, 0.3);
    border-radius: 18px;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
    color: $yellow;
    background: rgba(255, 212, 0, 0.06);
    font-size: 13px;
    font-weight: 600;
    transition:
        border-color 160ms ease,
        background-color 160ms ease,
        transform 160ms ease;

    &:hover,
    &:focus-visible {
        border-color: $yellow;
        background: rgba(255, 212, 0, 0.12);
        transform: translateY(-1px);
    }
}

.participants_card__add_icon {
    display: inline-flex;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    color: #111;
    background: $yellow;
    font-size: 12px;
}

.participants_list {
    margin: 0;
    padding: 0;
    list-style: none;
}

.participant_row {
    position: relative;
    display: flex;
    min-width: 0;
    // min-height: 68px;
    padding: 6px 0;
    border-bottom: 1px solid $border;
    align-items: center;
    gap: 11px;
    transition: background-color 160ms ease;

    &:last-child {
        border-bottom: 0;
    }

    &::before {
        position: absolute;
        top: 8px;
        bottom: 8px;
        left: -16px;
        width: 3px;
        border-radius: 0 3px 3px 0;
        content: '';
        background: transparent;
        transition: background-color 160ms ease;
    }

    &:hover,
    &:focus-within {
        background: linear-gradient(
            90deg,
            rgba(112, 200, 255, 0.04),
            transparent 65%
        );

        &::before {
            background: $blue;
        }
    }
}

.participant_row__avatar {
    display: block;
    width: 42px;
    height: 42px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    object-fit: cover;
    flex: 0 0 42px;
    background: $surface;
}

.participant_row__details {
    display: block;
    min-width: 0;
    flex: 1 1 auto;

    > span {
        display: block;
    }
}

.participant_row__name,
.participant_row__designation {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.participant_row__name {
    color: $text;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.35;
}

.participant_row__designation {
    margin-top: 2px;
    color: $muted;
    font-size: 12px;
    line-height: 1.3;
}

.participant_row__actions {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    justify-content: flex-end;
    gap: 7px;
    flex: 0 0 auto;
}

.admin_badge {
    display: inline-flex;
    min-height: 22px;
    padding: 0 7px;
    border: 1px solid rgba(112, 200, 255, 0.55);
    border-radius: 5px;
    align-items: center;
    color: $blue;
    background: rgba(112, 200, 255, 0.07);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.25px;
    line-height: 1;
    text-transform: uppercase;
}

.participant_menu {
    position: relative;
    display: inline-flex;
    flex: 0 0 auto;
}

.participant_menu__toggle {
    display: inline-flex;
    width: 32px;
    height: 32px;
    border: 1px solid transparent;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    color: $muted;
    transition:
        color 160ms ease,
        border-color 160ms ease,
        background-color 160ms ease;

    &:hover,
    &:focus-visible,
    &[aria-expanded='true'] {
        border-color: rgba(255, 212, 0, 0.3);
        color: $yellow;
        background: rgba(255, 212, 0, 0.08);
    }
}

.participant_menu__popup {
    position: absolute;
    top: calc(100% + 7px);
    right: 0;
    z-index: 20;
    width: 210px;
    padding: 6px;
    border: 1px solid rgba(255, 255, 255, 0.13);
    border-radius: 10px;
    background: #171717;
    box-shadow: 0 14px 38px rgba(0, 0, 0, 0.5);

    &::before {
        position: absolute;
        top: -5px;
        right: 11px;
        width: 9px;
        height: 9px;
        border-top: 1px solid rgba(255, 255, 255, 0.13);
        border-left: 1px solid rgba(255, 255, 255, 0.13);
        content: '';
        background: #171717;
        transform: rotate(45deg);
    }

    &--up {
        top: auto;
        bottom: calc(100% + 7px);

        &::before {
            top: auto;
            bottom: -5px;
            border-top: 0;
            border-left: 0;
            border-right: 1px solid rgba(255, 255, 255, 0.13);
            border-bottom: 1px solid rgba(255, 255, 255, 0.13);
        }
    }

    button {
        position: relative;
        z-index: 1;
        display: flex;
        width: 100%;
        min-height: 38px;
        padding: 8px 10px;
        border-radius: 7px;
        align-items: center;
        gap: 8px;
        color: $text;
        text-align: left;
        font-size: 14px;
        line-height: 1.25;
        transition:
            color 140ms ease,
            background-color 140ms ease;

        .fa {
            width: 15px;
            color: $muted;
            text-align: center;
        }

        &:hover,
        &:focus-visible {
            color: $yellow;
            background: rgba(255, 255, 255, 0.07);

            .fa {
                color: currentColor;
            }
        }

        &.participant_menu__danger {
            color: #ff8a84;

            &:hover,
            &:focus-visible {
                color: $danger;
                background: rgba(255, 77, 69, 0.1);
            }
        }
    }
}

.participant-menu-enter-active,
.participant-menu-leave-active {
    transition:
        opacity 130ms ease,
        transform 130ms ease;
    transform-origin: top right;
}

.participant-menu-enter,
.participant-menu-leave-to {
    opacity: 0;
    transform: translateY(-4px) scale(0.97);
}

.participant_menu__popup--up.participant-menu-enter-active,
.participant_menu__popup--up.participant-menu-leave-active {
    transform-origin: bottom right;
}

.participant_menu__popup--up.participant-menu-enter,
.participant_menu__popup--up.participant-menu-leave-to {
    transform: translateY(4px) scale(0.97);
}

.participants_card__empty {
    margin: 0;
    padding: 26px 10px;
    color: $muted;
    text-align: center;
    font-size: 13px;
}

.group_danger_action {
    display: flex;
    width: 100%;
    min-width: 0;
    min-height: 66px;
    margin-top: 15px;
    padding: 10px 14px;
    border: 1px solid rgba(255, 77, 69, 0.18);
    border-radius: 12px;
    align-items: center;
    gap: 11px;
    color: $danger;
    background: rgba(255, 77, 69, 0.045);
    text-align: left;
    transition:
        border-color 160ms ease,
        background-color 160ms ease,
        transform 160ms ease;

    &:hover,
    &:focus-visible {
        border-color: rgba(255, 77, 69, 0.55);
        background: rgba(255, 77, 69, 0.1);
        transform: translateY(-1px);
    }

    > span:nth-child(2) {
        display: block;
        min-width: 0;
        flex: 1 1 auto;
    }

    strong,
    small {
        display: block;
    }

    strong {
        color: inherit;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.35;
        text-transform: capitalize;
    }

    small {
        margin-top: 2px;
        overflow: hidden;
        color: $muted;
        font-size: 11px;
        line-height: 1.3;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.group_danger_action__icon {
    display: inline-flex;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    flex: 0 0 38px;
    background: rgba(255, 77, 69, 0.12);
    font-size: 16px;
}

.group_danger_action__arrow {
    flex: 0 0 auto;
    color: rgba(255, 255, 255, 0.32);
    font-size: 18px;
}

@media (max-width: 600px) {
    .group_info_panel__header {
        min-height: 58px;
        padding: 0 12px;
        flex-basis: 58px;
    }

    .group_info_panel__content {
        padding: 12px;
    }

    .group_profile_card__hero {
        height: 70px;
        margin-bottom: 44px;
    }

    .group_profile_card__avatar_shell {
        bottom: -41px;
        width: 88px;
        height: 88px;
    }

    .group_profile_card__body {
        padding: 0 12px 14px;
    }

    .group_profile_card__body > h3 {
        font-size: 17px;
    }

    .group_profile_card__management {
        margin-top: 14px;
        padding-top: 12px;
        gap: 7px;

        button {
            min-height: 48px;
            padding: 7px 8px;
            gap: 7px;
        }
    }

    .group_profile_card__management_icon {
        width: 29px;
        height: 29px;
        flex-basis: 29px;
    }

    .participants_card {
        margin-top: 12px;
        padding: 0 12px;
    }

    .participants_card__header {
        min-height: 62px;
    }

    .participants_card__add {
        width: 36px;
        padding: 0;
        justify-content: center;

        > span:last-child {
            position: absolute;
            width: 1px;
            height: 1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            clip-path: inset(50%);
            white-space: nowrap;
        }
    }

    .participants_card__add_icon {
        width: 26px;
        height: 26px;
    }

    .participant_row {
        // min-height: 64px;
        gap: 9px;
    }

    .participant_row__avatar {
        width: 38px;
        height: 38px;
        flex-basis: 38px;
    }

    .participant_row__actions {
        gap: 4px;
    }

    .admin_badge {
        max-width: 58px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .participant_menu__popup {
        width: 196px;
    }

    .group_danger_action {
        margin-top: 12px;
    }
}

@media (max-width: 370px) {
    .admin_badge {
        width: 8px;
        min-height: 8px;
        padding: 0;
        overflow: hidden;
        border: 0;
        border-radius: 50%;
        color: transparent;
        background: $success;
        font-size: 0;
    }

    .group_profile_card__management button small {
        display: none;
    }
}

@media (prefers-reduced-motion: reduce) {
    .group_info_panel *,
    .group_info_panel *::before,
    .group_info_panel *::after {
        scroll-behavior: auto !important;
        transition-duration: 0.01ms !important;
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
    }
}
</style>
